import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  IconButton,
  Alert,
  LinearProgress,
  Paper
} from '@mui/material';
import { PhotoCamera, Delete, CloudUpload } from '@mui/icons-material';
import axios from '../utils/api';

interface PhotoUploadProps {
  currentPhotoUrl?: string | null;
  lawyerId?: string;
  onPhotoChange?: (photoUrl: string | null) => void;
  onPhotoFile?: (file: File | null) => void; // For handling raw file in forms without lawyerId
  onError?: (error: string) => void;
  disabled?: boolean;
  size?: number;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  currentPhotoUrl,
  lawyerId,
  onPhotoChange,
  onPhotoFile,
  onError,
  disabled = false,
  size = 120
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentPhotoUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      onError?.('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      onError?.('File size must be less than 5MB');
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Upload file if lawyerId is provided, otherwise pass file to parent
    if (lawyerId) {
      uploadPhoto(file);
    } else {
      // For forms without lawyerId, pass the file to parent component
      onPhotoFile?.(file);
      onPhotoChange?.(url); // Use the blob URL as temporary photo URL
    }
  };

  const uploadPhoto = async (file: File) => {
    if (!lawyerId) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await axios.post(
        `/api/photos/${lawyerId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(progress);
          },
        }
      );

      if (response.data.success) {
        const photoUrl = response.data.lawyer.profilePhotoUrl;
        setPreviewUrl(photoUrl);
        onPhotoChange?.(photoUrl);
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Photo upload error:', error);
      onError?.(error.response?.data?.error || 'Failed to upload photo');
      
      // Reset preview on error
      if (currentPhotoUrl) {
        setPreviewUrl(currentPhotoUrl);
      } else {
        setPreviewUrl(null);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deletePhoto = async () => {
    if (lawyerId && currentPhotoUrl) {
      // Delete from server if lawyerId exists
      setUploading(true);

      try {
        await axios.delete(`/api/photos/${lawyerId}`);
        
        setPreviewUrl(null);
        onPhotoChange?.(null);
      } catch (error: any) {
        console.error('Photo deletion error:', error);
        onError?.(error.response?.data?.error || 'Failed to delete photo');
      } finally {
        setUploading(false);
      }
    } else {
      // Just clear the preview for forms without lawyerId
      setPreviewUrl(null);
      onPhotoChange?.(null);
      onPhotoFile?.(null);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const getInitials = () => {
    // This would need to be passed as a prop in a real implementation
    return 'JD'; // Placeholder
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          borderRadius: '50%',
          overflow: 'hidden',
          width: size,
          height: size,
        }}
      >
        <Avatar
          src={previewUrl || undefined}
          sx={{
            width: size,
            height: size,
            fontSize: `${size * 0.3}px`,
            bgcolor: 'primary.main',
          }}
        >
          {previewUrl ? '' : getInitials()}
        </Avatar>
        
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
            }}
          >
            <Box sx={{ width: '80%', textAlign: 'center' }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ mb: 1 }}
              />
              <Typography variant="caption">
                {uploadProgress}%
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />
        
        <Button
          variant="outlined"
          startIcon={<PhotoCamera />}
          onClick={triggerFileSelect}
          disabled={disabled || uploading}
          size="small"
        >
          {previewUrl ? 'Change Photo' : 'Upload Photo'}
        </Button>

        {previewUrl && (
          <Button
            variant="text"
            color="error"
            startIcon={<Delete />}
            onClick={deletePhoto}
            disabled={disabled || uploading}
            size="small"
          >
            Remove
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" textAlign="center">
        Max size: 5MB<br />
        Formats: JPEG, PNG, WebP
      </Typography>
    </Box>
  );
};

export default PhotoUpload;
